import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AuthPageLayout } from 'components/layout';
import { ContentBox } from 'components/content';
import { Checkbox } from '@clariness/checkbox';
import { Flex } from '@clariness/flex';
import { Box } from '@clariness/box';
import { Text } from '@clariness/clarikit-react';
import { ShouldRender } from 'components/ShouldRender';
import { BoxLoader } from 'components/loaders';

type UserAgreementViewProps = {
  isLoading: boolean;
  hasAgreed: boolean;
  shouldShowPrompt: boolean;
  onScrollBottom: () => void;
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  checkboxTestId?: string;
};

export const UserAgreementView = ({
  isLoading,
  hasAgreed,
  shouldShowPrompt,
  onScrollBottom,
  onCheckboxChange,
  checkboxTestId = 'register-agreement-checkbox',
}: UserAgreementViewProps): React.ReactElement => {
  return (
    <React.Fragment>
      <AuthPageLayout.Heading>
        <FormattedMessage
          id="is.register.end_user_agreement_heading"
          defaultMessage="End User Agreement"
        />
      </AuthPageLayout.Heading>

      <ContentBox onScrollBottom={onScrollBottom}>
        <div style={{ whiteSpace: 'pre-line' }}>
          <FormattedMessage
            id="is.register.end_user_agreement"
            defaultMessage={`I hereby confirm that I only have access to and process that data for those studies in which I act as the PI (or as a medical assistant, such as a study nurse) and for which I have been approved to act in by the ethics committee.

I hereby confirm that all data I have access to complies with all relevant statutory data protection regulations as well as all other relevant legal and ethical guidelines.

All data forwarded to me here is at all times subjected to the obligation to maintain confidentiality.

I am aware that all personal data from interested volunteers forwarded to me here can be accepted and recorded solely for the purpose of patient recruitment, and only in order for the study to make contact with those interested volunteers.

Data from interested volunteers made available here is not to be seen or used as Source Data.`}
          />
        </div>
      </ContentBox>

      <Flex
        sx={{
          alignItems: 'center',
          gap: '5px',
          mt: '39px',
        }}
      >
        <Checkbox
          checked={hasAgreed}
          onChange={onCheckboxChange}
          data-test-id={checkboxTestId}
        />

        <Text as="p">
          <FormattedMessage
            id="is.register_end_user_agreement_checkbox"
            defaultMessage="I agree to the terms of use."
          />
        </Text>
      </Flex>

      <ShouldRender when={shouldShowPrompt}>
        <Box sx={{ mt: '15px' }}>
          <Text as="p" color={'color-red-variant-1'}>
            <FormattedMessage
              id="is.register_end_user_agreement_prompt_message"
              defaultMessage="Please scroll through the End of User Agreement first."
            />
          </Text>
        </Box>
      </ShouldRender>

      <ShouldRender when={isLoading}>
        <BoxLoader />
      </ShouldRender>
    </React.Fragment>
  );
};
